import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { useLocation } from 'wouter';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { getJson } from '@/lib/http';

type InclusionStatus = 'INCLUDED' | 'EXCLUDED';

type LatestAiTriageSummary = {
  resultId: number;
  flagged: boolean;
  reasons: string[];
  confidence: number;
  modelVersion: string;
  createdAt: string | null;
};

type LatestAiTriageEvaluationSummary = {
  evaluationId: number;
  evaluationKind: string;
  humanDecision: string;
  agreement: boolean;
  disagreementType: string | null;
  humanReasonCodes: string[];
  createdAt: string | null;
};

type GoldCandidateRecord = {
  postingEventId: number;
  evidenceAssetId: number | null;
  reviewDecisionId: number | null;
  evidenceRole: string | null;
  assetType: string | null;
  hasPublicId: boolean;
  hasSecureUrl: boolean;
  humanOutcome: 'APPROVE' | 'REJECT' | null;
  retailerReady: boolean | null;
  rejectionType: string | null;
  humanReasonCodes: string[];
  normalisedIssueFamilies: string[];
  expectedFormatMatch: string | null;
  latestAiTriage: LatestAiTriageSummary | null;
  latestAiTriageEvaluation: LatestAiTriageEvaluationSummary | null;
  inclusionStatus: InclusionStatus;
  exclusionReasons: string[];
};

type GoldCandidateAggregates = {
  inclusionStatus?: Record<string, number>;
  exclusionReason?: Record<string, number>;
  humanOutcome?: Record<string, number>;
  evidenceRole?: Record<string, number>;
  assetType?: Record<string, number>;
  humanReasonCode?: Record<string, number>;
  normalisedIssueFamily?: Record<string, number>;
  retailerReady?: Record<string, number>;
  aiAgreement?: Record<string, number>;
};

type GoldCandidatesResponse = {
  ok: true;
  records: GoldCandidateRecord[];
  aggregates: GoldCandidateAggregates;
  pagination: {
    limit: number;
    offset: number;
    returned: number;
    totalAfterFilters: number;
  };
  meta?: {
    readOnly: true;
    scanLimit: number;
    scannedRecords: number;
    detectionNotes?: Record<string, string>;
  };
};

type IncludedFilter = 'all' | 'true' | 'false';
type DecisionFilter = 'all' | 'APPROVE' | 'REJECT';
type EvidenceRoleFilter = 'all' | 'POSTING_STANDARD' | 'REMOVAL_BEFORE' | 'REMOVAL_AFTER';
type RetailerReadyFilter = 'all' | 'true' | 'false' | 'null';

function formatNumber(value: number | undefined | null): string {
  return Number(value ?? 0).toLocaleString();
}

function buildQueryString(filters: {
  included: IncludedFilter;
  decision: DecisionFilter;
  evidenceRole: EvidenceRoleFilter;
  retailerReady: RetailerReadyFilter;
  reasonCode: string;
  limit: string;
}): string {
  const params = new URLSearchParams();
  params.set('limit', filters.limit);
  if (filters.included !== 'all') params.set('included', filters.included);
  if (filters.decision !== 'all') params.set('decision', filters.decision);
  if (filters.evidenceRole !== 'all') params.set('evidenceRole', filters.evidenceRole);
  if (filters.retailerReady !== 'all') params.set('retailerReady', filters.retailerReady);
  const reasonCode = filters.reasonCode.trim().toUpperCase();
  if (reasonCode) params.set('reasonCode', reasonCode);
  return params.toString();
}

function statusVariant(value: string | null | undefined): 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' | 'info' {
  if (value === 'INCLUDED' || value === 'APPROVE' || value === 'true') return 'success';
  if (value === 'EXCLUDED' || value === 'REJECT' || value === 'false') return 'warning';
  return 'outline';
}

function ChipList({ values, empty = 'None' }: { values?: string[] | null; empty?: string }) {
  if (!values || values.length === 0) {
    return <span className="text-muted-foreground">{empty}</span>;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {values.map((value) => (
        <Badge key={value} variant="outline" className="text-[10px]">
          {value}
        </Badge>
      ))}
    </div>
  );
}

function AggregateList({ title, data }: { title: string; data?: Record<string, number> }) {
  const entries = Object.entries(data ?? {}).sort((a, b) => b[1] - a[1]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">No data</p>
        ) : (
          <div className="space-y-2">
            {entries.map(([key, count]) => (
              <div key={key} className="flex items-center justify-between gap-3 text-sm">
                <span className="truncate text-muted-foreground">{key}</span>
                <Badge variant={statusVariant(key)}>{formatNumber(count)}</Badge>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SummaryCard({ title, value, description }: { title: string; value: number | undefined; description?: string }) {
  return (
    <Card>
      <CardContent className="pt-4">
        <p className="text-xs font-medium text-muted-foreground">{title}</p>
        <p className="mt-1 text-2xl font-semibold">{formatNumber(value)}</p>
        {description && <p className="mt-1 text-xs text-muted-foreground">{description}</p>}
      </CardContent>
    </Card>
  );
}

function BooleanBadge({ value }: { value: boolean | null }) {
  if (value == null) return <Badge variant="outline">null</Badge>;
  return <Badge variant={value ? 'success' : 'secondary'}>{String(value)}</Badge>;
}

function LatestAiTriageCell({ triage }: { triage: LatestAiTriageSummary | null }) {
  if (!triage) return <span className="text-muted-foreground">None</span>;

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap gap-1">
        <Badge variant={triage.flagged ? 'warning' : 'success'}>{triage.flagged ? 'flagged' : 'clear'}</Badge>
        <Badge variant="outline">{Math.round(triage.confidence * 100)}%</Badge>
      </div>
      <p className="text-[11px] text-muted-foreground">{triage.modelVersion}</p>
      <ChipList values={triage.reasons} empty="No reasons" />
    </div>
  );
}

function LatestAiEvaluationCell({ evaluation }: { evaluation: LatestAiTriageEvaluationSummary | null }) {
  if (!evaluation) return <span className="text-muted-foreground">None</span>;

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap gap-1">
        <Badge variant={evaluation.agreement ? 'success' : 'warning'}>
          {evaluation.agreement ? 'agreement' : 'disagreement'}
        </Badge>
        <Badge variant="outline">{evaluation.humanDecision}</Badge>
      </div>
      {evaluation.disagreementType && (
        <p className="text-[11px] text-muted-foreground">{evaluation.disagreementType}</p>
      )}
      <ChipList values={evaluation.humanReasonCodes} empty="No reasons" />
    </div>
  );
}

export default function AIGoldCandidatesPage() {
  const [, setLocation] = useLocation();
  const [included, setIncluded] = useState<IncludedFilter>('all');
  const [decision, setDecision] = useState<DecisionFilter>('all');
  const [evidenceRole, setEvidenceRole] = useState<EvidenceRoleFilter>('all');
  const [retailerReady, setRetailerReady] = useState<RetailerReadyFilter>('all');
  const [reasonCode, setReasonCode] = useState('');
  const [limit, setLimit] = useState('100');

  const queryString = useMemo(
    () => buildQueryString({ included, decision, evidenceRole, retailerReady, reasonCode, limit }),
    [included, decision, evidenceRole, retailerReady, reasonCode, limit],
  );

  const reportQuery = useQuery<GoldCandidatesResponse>({
    queryKey: ['/api/admin/ai/gold-candidates', queryString],
    queryFn: () => getJson<GoldCandidatesResponse>(`/api/admin/ai/gold-candidates?${queryString}`),
  });

  const data = reportQuery.data;
  const aggregates = data?.aggregates;
  const isFiltered = included !== 'all'
    || decision !== 'all'
    || evidenceRole !== 'all'
    || retailerReady !== 'all'
    || reasonCode.trim().length > 0
    || limit !== '100';

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Reviewed test photos</h1>
          <p className="text-sm text-muted-foreground max-w-3xl">
            Store photos your team already approved or rejected, filtered for use in locked test sets.
            This list is read-only — it does not change reviews.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setLocation('/admin/ai')}>
            Home
          </Button>
          <Button onClick={() => setLocation('/admin/ai/results')}>
            Try a change
          </Button>
          <Button variant="outline" onClick={() => reportQuery.refetch()} disabled={reportQuery.isFetching}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Supporting detail for Try a change</AlertTitle>
        <AlertDescription>
          Use this list to understand which reviewed photos can feed a locked test set.
          Everyday trial work starts from Try a change, not this page.
        </AlertDescription>
      </Alert>

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        <SummaryCard title="Included candidates" value={aggregates?.inclusionStatus?.INCLUDED} />
        <SummaryCard title="Excluded candidates" value={aggregates?.inclusionStatus?.EXCLUDED} />
        <SummaryCard title="Total scanned" value={data?.meta?.scannedRecords} description={`Cap ${formatNumber(data?.meta?.scanLimit)}`} />
        <SummaryCard title="Approvals" value={aggregates?.humanOutcome?.APPROVE} />
        <SummaryCard title="Rejections" value={aggregates?.humanOutcome?.REJECT} />
        <SummaryCard title="Retailer-ready true" value={aggregates?.retailerReady?.true} />
        <SummaryCard title="No review decision" value={aggregates?.exclusionReason?.NO_REVIEW_DECISION} />
        <SummaryCard title="No usable image" value={aggregates?.exclusionReason?.NO_USABLE_IMAGE} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>
            Changing filters refetches the read-only endpoint. {isFiltered ? 'Filters are active.' : 'Showing default report.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-6">
            <Select value={included} onValueChange={(value) => setIncluded(value as IncludedFilter)}>
              <SelectTrigger>
                <SelectValue placeholder="Included" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All inclusion</SelectItem>
                <SelectItem value="true">Included</SelectItem>
                <SelectItem value="false">Excluded</SelectItem>
              </SelectContent>
            </Select>

            <Select value={decision} onValueChange={(value) => setDecision(value as DecisionFilter)}>
              <SelectTrigger>
                <SelectValue placeholder="Decision" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All decisions</SelectItem>
                <SelectItem value="APPROVE">APPROVE</SelectItem>
                <SelectItem value="REJECT">REJECT</SelectItem>
              </SelectContent>
            </Select>

            <Select value={evidenceRole} onValueChange={(value) => setEvidenceRole(value as EvidenceRoleFilter)}>
              <SelectTrigger>
                <SelectValue placeholder="Evidence role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All roles</SelectItem>
                <SelectItem value="POSTING_STANDARD">POSTING_STANDARD</SelectItem>
                <SelectItem value="REMOVAL_BEFORE">REMOVAL_BEFORE</SelectItem>
                <SelectItem value="REMOVAL_AFTER">REMOVAL_AFTER</SelectItem>
              </SelectContent>
            </Select>

            <Select value={retailerReady} onValueChange={(value) => setRetailerReady(value as RetailerReadyFilter)}>
              <SelectTrigger>
                <SelectValue placeholder="Retailer-ready" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All retailer-ready</SelectItem>
                <SelectItem value="true">true</SelectItem>
                <SelectItem value="false">false</SelectItem>
                <SelectItem value="null">null</SelectItem>
              </SelectContent>
            </Select>

            <Input
              value={reasonCode}
              onChange={(event) => setReasonCode(event.target.value)}
              placeholder="Reason code"
              aria-label="Reason code"
            />

            <Select value={limit} onValueChange={setLimit}>
              <SelectTrigger>
                <SelectValue placeholder="Limit" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25 rows</SelectItem>
                <SelectItem value="50">50 rows</SelectItem>
                <SelectItem value="100">100 rows</SelectItem>
                <SelectItem value="250">250 rows</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {reportQuery.isLoading && (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">Loading gold candidate report…</CardContent>
        </Card>
      )}

      {reportQuery.isError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Unable to load gold candidates</AlertTitle>
          <AlertDescription>
            {reportQuery.error instanceof Error ? reportQuery.error.message : 'Unknown error'}
          </AlertDescription>
        </Alert>
      )}

      {data && (
        <>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <AggregateList title="Inclusion Status" data={aggregates?.inclusionStatus} />
            <AggregateList title="Exclusion Reasons" data={aggregates?.exclusionReason} />
            <AggregateList title="Human Outcome" data={aggregates?.humanOutcome} />
            <AggregateList title="Evidence Role" data={aggregates?.evidenceRole} />
            <AggregateList title="Asset Type" data={aggregates?.assetType} />
            <AggregateList title="Retailer Ready" data={aggregates?.retailerReady} />
            <AggregateList title="Issue Families" data={aggregates?.normalisedIssueFamily} />
            <AggregateList title="Human Reason Codes" data={aggregates?.humanReasonCode} />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Candidate Records</CardTitle>
              <CardDescription>
                Showing {formatNumber(data.pagination.returned)} of {formatNumber(data.pagination.totalAfterFilters)} filtered records.
                Scanned {formatNumber(data.meta?.scannedRecords)} records with cap {formatNumber(data.meta?.scanLimit)}.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {data.records.length === 0 ? (
                <div className="py-8 text-sm text-muted-foreground">No candidate records match the current filters.</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Posting</TableHead>
                        <TableHead>Evidence</TableHead>
                        <TableHead>Review</TableHead>
                        <TableHead>Role / Asset</TableHead>
                        <TableHead>Locators</TableHead>
                        <TableHead>Outcome</TableHead>
                        <TableHead>Retailer Ready</TableHead>
                        <TableHead>Rejection</TableHead>
                        <TableHead>Reason Codes</TableHead>
                        <TableHead>Issue Families</TableHead>
                        <TableHead>Expected Format</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Exclusions</TableHead>
                        <TableHead>Latest AI Triage</TableHead>
                        <TableHead>Latest AI Evaluation</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.records.map((record) => (
                        <TableRow key={`${record.postingEventId}-${record.evidenceAssetId ?? 'no-asset'}`}>
                          <TableCell className="text-xs font-mono">{record.postingEventId}</TableCell>
                          <TableCell className="text-xs font-mono">{record.evidenceAssetId ?? 'null'}</TableCell>
                          <TableCell className="text-xs font-mono">{record.reviewDecisionId ?? 'null'}</TableCell>
                          <TableCell className="min-w-[160px] text-xs">
                            <div>{record.evidenceRole ?? 'null'}</div>
                            <div className="text-muted-foreground">{record.assetType ?? 'null'}</div>
                          </TableCell>
                          <TableCell className="min-w-[120px] text-xs">
                            <div>publicId: {String(record.hasPublicId)}</div>
                            <div>secureUrl: {String(record.hasSecureUrl)}</div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={statusVariant(record.humanOutcome)}>{record.humanOutcome ?? 'null'}</Badge>
                          </TableCell>
                          <TableCell>
                            <BooleanBadge value={record.retailerReady} />
                          </TableCell>
                          <TableCell className="text-xs">{record.rejectionType ?? 'null'}</TableCell>
                          <TableCell className="min-w-[180px]">
                            <ChipList values={record.humanReasonCodes} />
                          </TableCell>
                          <TableCell className="min-w-[180px]">
                            <ChipList values={record.normalisedIssueFamilies} />
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{record.expectedFormatMatch ?? 'null'}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={statusVariant(record.inclusionStatus)}>{record.inclusionStatus}</Badge>
                          </TableCell>
                          <TableCell className="min-w-[220px]">
                            <ChipList values={record.exclusionReasons} />
                          </TableCell>
                          <TableCell className="min-w-[220px]">
                            <LatestAiTriageCell triage={record.latestAiTriage} />
                          </TableCell>
                          <TableCell className="min-w-[220px]">
                            <LatestAiEvaluationCell evaluation={record.latestAiTriageEvaluation} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
